import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TOP_LEVEL_NAV, TopLevelNavId, TopLevelNavItem, findActiveTopLevel } from '../navigation.config';

const STORAGE_KEY = 'triggerbee.topLevelSideMenu.pinned';

type PinState = 'none' | 'collapsed' | 'expanded';

@Injectable({ providedIn: 'root' })
export class TopLevelSideMenuService {
  private readonly router = inject(Router);

  public readonly pinState = signal<PinState>(this.readPinStateFromStorage());
  public readonly hovered = signal<boolean>(false);
  // Mobile uses a simple open/hidden overlay instead of the desktop pin/hover rail.
  public readonly mobileOpen = signal<boolean>(false);
  public readonly pinned = computed<boolean>(() => this.pinState() !== 'none');
  public readonly expanded = computed<boolean>(() => {
    const state = this.pinState();
    if (state === 'expanded') return true;
    if (state === 'collapsed') return false;
    return this.hovered();
  });

  public readonly currentUrl = signal<string>(this.router.url);
  private readonly manualOverride = signal<TopLevelNavId | null>(null);
  public readonly activeTopLevel = computed<TopLevelNavItem | undefined>(() => {
    const override = this.manualOverride();
    if (override) {
      return TOP_LEVEL_NAV.find((item) => item.id === override);
    }
    return findActiveTopLevel(this.currentUrl());
  });

  constructor() {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((event) => {
      this.currentUrl.set(event.urlAfterRedirects);
      // Only clear the manual override when we navigate outside the overridden item's URL space.
      // This ensures that clicking a sub-item (e.g. "Objectives Dashboard" under Dashboard) keeps the
      // manually selected top-level active instead of switching to whichever item findActiveTopLevel picks.
      const override = this.manualOverride();
      if (override) {
        const path = event.urlAfterRedirects.split('?')[0];
        const item = TOP_LEVEL_NAV.find((i) => i.id === override);
        const stillInSection = item?.urlPrefixes.some((prefix) =>
          prefix === '/' ? path === '/' : path === prefix || path.startsWith(prefix + '/'),
        );
        if (!stillInSection) {
          this.manualOverride.set(null);
        }
      }
      // Navigating away always dismisses the mobile overlay menu.
      this.mobileOpen.set(false);
    });
  }

  public toggleMobileOpen(): void {
    this.mobileOpen.update((open) => !open);
  }

  public closeMobile(): void {
    this.mobileOpen.set(false);
  }

  public selectTopLevel(id: TopLevelNavId): void {
    this.manualOverride.set(id);
  }

  // Clicking the pin locks the rail at whatever state it is currently shown in:
  // pin while expanded → 'expanded' (stays open, ignores hover-out);
  // pin while collapsed → 'collapsed' (stays closed, ignores hover-in);
  // pin while already pinned → unpin and resume hover behavior.
  public togglePin(): void {
    const current = this.pinState();
    let next: PinState;
    if (current !== 'none') {
      next = 'none';
    } else {
      next = this.hovered() ? 'expanded' : 'collapsed';
    }
    this.pinState.set(next);
    this.writePinStateToStorage(next);
    if (next === 'none') {
      // Force-clear hover on unpin so the menu shrinks immediately even when the cursor is still over it.
      // Next mouseenter will re-expand it normally.
      this.hovered.set(false);
    }
  }

  public setHovered(value: boolean): void {
    this.hovered.set(value);
  }

  private readPinStateFromStorage(): PinState {
    try {
      const value = window.localStorage.getItem(STORAGE_KEY);
      if (value === 'expanded' || value === 'collapsed' || value === 'none') return value;
      if (value === 'true') return 'expanded'; // legacy boolean storage
      return 'none';
    } catch {
      return 'none';
    }
  }

  private writePinStateToStorage(value: PinState): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // localStorage may be unavailable (private mode, quota); pin state remains in-memory.
    }
  }
}
